import { useQuery } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Answer } from '@/components/common/Answer';
import { ExportButton } from '@/components/common/ExportButton';
import { SkeletonLoader } from '@/components/common/StateViews';
import { fetchCadeSei } from '@/services/cade';
import type { CadeCaseSei, CadeSeiDocument } from '@/services/types';

/** The refusal that is about the CORPUS (the ledger is not loaded here), not this process. */
const NO_TABLES = 'substrate.reasons.cadeSeiNoTables';

/**
 * The process's milestone documents in CADE's public SEI: the SG's opening, technical notes,
 * opinions, reports, votes, the judgment certificate, embargos, the trânsito em julgado.
 *
 * A third source beside the register and the session archive, dated by SEI and shown in its own
 * lane: every other document (e-mails, letters, attendance lists) is counted, not listed. A vote
 * is placed under an archive session by its signature date, and the rule is stated; nothing here
 * reads a document, so no outcome is derived from one existing.
 */
export function SeiPanel({ caseKey }: { caseKey: string }) {
  const { t, i18n } = useTranslation(['cade']);
  const { data, isPending, error } = useQuery({
    queryKey: ['cade', 'sei', caseKey],
    queryFn: () => fetchCadeSei(caseKey),
    retry: false,
  });

  if (isPending) return <SkeletonLoader rows={3} />;
  // A failed read must not break the page it sits on.
  if (error) return null;

  const day = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeZone: 'UTC' });
  const number = new Intl.NumberFormat(i18n.language);
  const scope = data.sei.reason_key === NO_TABLES ? 'corpus' : 'record';

  return (
    <section className="space-y-3 rounded-lg border bg-card p-4" data-sei>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-foreground">{t('cade:case.sei.title')}</h2>
        <ExportButton path={`/api/v1/cade/export/c/${encodeURIComponent(caseKey)}/sei`} />
      </div>
      <p className="text-xs text-muted-foreground">{t('cade:case.sei.subtitle')}</p>
      <Answer answer={data.sei} scope={scope} help={t('cade:case.sei.help', { after: data.vote_after_days, before: data.vote_before_days })}>
        {(sei: CadeCaseSei) => (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground" data-sei-counts>
              {t('cade:case.sei.counts', {
                nup: sei.nup,
                listed: number.format(sei.milestones.length),
                total: number.format(sei.documents_total),
              })}
            </p>
            {sei.milestones.length > 0 ? (
              <ol className="space-y-1.5 border-l pl-4 text-sm">
                {sei.milestones.map((doc) => (
                  <li key={doc.sei_doc_number} data-sei-doc={doc.kind}>
                    <span className="tabular-nums text-foreground">
                      {doc.doc_date ? day.format(new Date(`${doc.doc_date}T00:00:00Z`)) : '—'}
                    </span>
                    <span className="ml-2 text-muted-foreground">
                      {t(`cade:case.sei.kinds.${doc.kind}`)}
                    </span>
                    <DocLink doc={doc} />
                    {doc.unit ? (
                      <span className="ml-2 text-xs text-muted-foreground">{doc.unit}</span>
                    ) : null}
                    {doc.session_date ? (
                      <span className="ml-2 text-xs text-muted-foreground">
                        {t('cade:case.sei.placed', {
                          date: day.format(new Date(`${doc.session_date}T00:00:00Z`)),
                        })}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ol>
            ) : null}
            <p className="text-xs text-muted-foreground" data-sei-votes>
              {t('cade:case.sei.votes', {
                placed: number.format(sei.votes_placed),
                total: number.format(sei.votes_total),
              })}
            </p>
          </div>
        )}
      </Answer>
    </section>
  );
}

/** A document's SEI number, linked to SEI's public address with the day that address was read. */
export function DocLink({ doc }: { doc: CadeSeiDocument }) {
  const { t, i18n } = useTranslation(['cade']);
  const label = `${doc.doc_type} · SEI ${doc.sei_doc_number}`;
  if (!doc.url) return <span className="ml-2 font-medium text-foreground">{label}</span>;
  const read = doc.read_on
    ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeZone: 'UTC' }).format(
        new Date(`${doc.read_on}T00:00:00Z`),
      )
    : null;
  return (
    <a
      href={doc.url}
      target="_blank"
      rel="noopener noreferrer"
      className="ml-2 inline-flex items-center gap-1 font-medium text-primary underline underline-offset-4"
      title={read ? t('cade:case.sei.readOn', { date: read }) : undefined}
    >
      {label}
      <ExternalLink aria-hidden="true" className="size-3" />
    </a>
  );
}
